import { useContext, useEffect, useState } from 'react';
import { MagnifyingGlassIcon, ScaleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from '../../../utils/axios';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';

/* ── Helpers ───────────────────────────────────────────────────────── */
function outcomeBadge(s: string) {
  const l = s?.toLowerCase();
  if (l?.includes('convicted') || l?.includes('guilty'))    return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
  if (l?.includes('acquitted') || l?.includes('dismissed')) return 'bg-red-500/15 text-red-400 border-red-500/20';
  return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
}

interface PrecedentForm {
  title: string;
  author: string;
  content: string;
  species: string;
  court: string;
  year: string;
  outcome: string;
  judge: string;
  sentence: string;
  reference: string;
  summary: string;
}

const EMPTY_FORM: PrecedentForm = {
  title: '', author: '', content: '', species: '', court: '',
  year: '', outcome: '', judge: '', sentence: '', reference: '', summary: '',
};

/* ── PrecedentModal ────────────────────────────────────────────────── */
function PrecedentModal({
  initial, token, onClose, onSaved,
}: {
  initial?: any;
  token: string;
  onClose: () => void;
  onSaved: (p: any, isEdit: boolean) => void;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState<PrecedentForm>(
    isEdit
      ? {
          title:     initial.title     ?? '',
          author:    initial.author    ?? '',
          content:   initial.content   ?? '',
          species:   initial.species   ?? '',
          court:     initial.court     ?? '',
          year:      initial.year      ?? '',
          outcome:   initial.outcome   ?? '',
          judge:     initial.judge     ?? '',
          sentence:  initial.sentence  ?? '',
          reference: initial.reference ?? '',
          summary:   initial.summary   ?? '',
        }
      : { ...EMPTY_FORM },
  );
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k: keyof PrecedentForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.author.trim() || !form.content.trim()) {
      setError('Title, Author and Content are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = isEdit
        ? await axios.put(`/precedents/${initial.id}`, form, { headers })
        : await axios.post('/precedents/create', form, { headers });
      onSaved(res.data?.data ?? res.data, isEdit);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to save precedent.');
    } finally {
      setSaving(false);
    }
  }

  const textField = (label: string, k: keyof PrecedentForm, placeholder = '') => (
    <div>
      <label className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">{label}</label>
      <input
        type="text"
        value={form[k]}
        onChange={set(k)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full max-w-2xl rounded-2xl border border-white/8 overflow-hidden flex flex-col" style={{ background: '#071a0f', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/6 shrink-0 flex items-center justify-between">
          <div>
            <p className="text-green-400 text-[10px] uppercase tracking-widest mb-0.5">{isEdit ? 'Edit' : 'Add'} Precedent</p>
            <h3 className="text-white/85 text-sm font-semibold">{isEdit ? `Editing: ${initial.title}` : 'New Legal Precedent'}</h3>
          </div>
          <button onClick={onClose} className="text-white/25 hover:text-white/60 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">{textField('Title *', 'title', 'e.g. Republic v. John Doe — Ivory Trafficking 2022')}</div>
            {textField('Author *', 'author', 'e.g. Kenya Wildlife Service')}
            {textField('Year', 'year', 'e.g. 2022')}
            {textField('Court', 'court', 'e.g. Nairobi High Court')}
            {textField('Species', 'species', 'e.g. Elephant, Lion')}
            {textField('Outcome', 'outcome', 'e.g. Convicted')}
            {textField('Judge', 'judge', 'e.g. Hon. J. Mwangi')}
            {textField('Sentence', 'sentence', 'e.g. 5 years + KES 2M fine')}
            {textField('Reference', 'reference', 'e.g. Criminal Case No. 123/2022')}
          </div>

          <div className="mb-3">
            <label htmlFor="precedent-summary" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">Summary</label>
            <textarea
              id="precedent-summary"
              value={form.summary}
              onChange={set('summary')}
              rows={2}
              placeholder="Brief case summary..."
              className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 resize-none"
            />
          </div>

          <div>
            <label htmlFor="precedent-content" className="block text-white/40 text-[10px] uppercase tracking-wider mb-1">Full Content *</label>
            <textarea
              id="precedent-content"
              value={form.content}
              onChange={set('content')}
              rows={5}
              placeholder="Full legal case details, ruling, and analysis..."
              className="w-full bg-white/5 border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 resize-none"
            />
          </div>

          {error && <p className="mt-3 text-red-400 text-xs">{error}</p>}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/6 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs text-white/50 hover:text-white/80 transition-colors">Cancel</button>
          <button
            onClick={submit as any}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Precedent'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── DeleteConfirm ─────────────────────────────────────────────────── */
function DeleteConfirm({ title, onConfirm, onCancel, deleting }: { title: string; onConfirm: () => void; onCancel: () => void; deleting: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div className="w-full max-w-sm rounded-2xl border border-red-500/20 p-6" style={{ background: '#1a0505' }}>
        <p className="text-white/70 text-sm font-semibold mb-1">Delete Precedent</p>
        <p className="text-white/40 text-xs mb-5">Are you sure you want to delete <span className="text-white/65">&ldquo;{title}&rdquo;</span>? This cannot be undone.</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-xs text-white/50 hover:text-white/80 transition-colors">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-xs font-semibold transition-colors disabled:opacity-50"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main View ─────────────────────────────────────────────────────── */
export default function CasePrecedentsView({ canEdit = false }: { canEdit?: boolean }) {
  const { state } = useContext(AuthContext);
  const token = state.userToken ?? '';

  const [precedents, setPrecedents] = useState<any[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [expanded,   setExpanded]   = useState<number | null>(null);
  const [showModal,  setShowModal]  = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting,   setDeleting]   = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get('/precedents/all')
      .then((res) => {
        if (res.status === 200) setPrecedents(res.data?.data ?? res.data ?? []);
      })
      .catch((_err) => { /* non-critical — user sees empty list */ })
      .finally(() => setLoading(false));
  }, []);

  const filtered = precedents.filter((p: any) => {
    const q = search.toLowerCase();
    return !q ||
      p.title?.toLowerCase().includes(q) ||
      p.species?.toLowerCase().includes(q) ||
      p.court?.toLowerCase().includes(q);
  });

  function onSaved(p: any, isEdit: boolean) {
    if (isEdit) {
      setPrecedents((prev) => prev.map((x) => x.id === p.id ? p : x));
    } else {
      setPrecedents((prev) => [p, ...prev]);
    }
    setEditTarget(null);
    setShowModal(false);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`/precedents/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrecedents((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setExpanded(null);
    } catch { /* silent */ }
    finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
            Case Precedents
          </h2>
          <p className="text-white/30 text-xs mt-0.5">{precedents.length} legal precedents on record</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
            <input
              className="bg-white/5 border border-white/10 text-white text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20 w-52"
              placeholder="Search by title, species, court..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={() => { setEditTarget(null); setShowModal(true); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all"
            >
              <PlusIcon className="w-3.5 h-3.5" /> Add Precedent
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 rounded-full border-2 border-green-500/30 border-t-green-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <ScaleIcon className="w-12 h-12 text-white/10" />
          <p className="text-white/25 text-sm">No precedents found</p>
          {canEdit && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 rounded-full bg-green-700 text-white text-xs font-semibold hover:bg-green-600 transition-all"
            >
              Add First Precedent
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((p: any, idx: number) => {
            const isOpen = expanded === p.id ?? idx;
            const key = p.id ?? idx;
            return (
              <div key={key} className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
                <div
                  role="button"
                  tabIndex={0}
                  className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer hover:bg-white/3 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : key)}
                  onKeyDown={(e) => e.key === 'Enter' && setExpanded(isOpen ? null : key)}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-green-400 text-[10px] font-bold tabular-nums"
                      style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}
                    >
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white/85 text-sm font-semibold truncate">{p.title || `Case #${idx + 1}`}</p>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {p.author  && <span className="text-white/30 text-[10px]">{p.author}</span>}
                        {p.species && <span className="text-white/35 text-xs">{p.species}</span>}
                        {p.court   && <span className="text-white/25 text-xs">&bull; {p.court}</span>}
                        {p.year    && <span className="text-white/25 text-xs">&bull; {p.year}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {p.outcome && (
                      <span className={`px-2.5 py-1 rounded-full border text-[10px] font-semibold ${outcomeBadge(p.outcome)}`}>
                        {p.outcome}
                      </span>
                    )}
                    <span className={`text-white/25 text-xs transition-transform inline-block ${isOpen ? 'rotate-180' : ''}`}>▾</span>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-white/5 pt-4" style={{ background: 'rgba(0,0,0,0.1)' }}>
                    {p.summary && (
                      <p className="text-white/55 text-xs leading-relaxed mb-4">{p.summary}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      {[
                        ['Court',     p.court],
                        ['Year',      p.year],
                        ['Outcome',   p.outcome],
                        ['Judge',     p.judge],
                        ['Sentence',  p.sentence],
                        ['Reference', p.reference],
                      ].map(([k, v]) => v ? (
                        <div key={k as string}>
                          <p className="text-white/22 text-[9px] uppercase tracking-wider mb-0.5">{k}</p>
                          <p className="text-white/65 text-xs">{v}</p>
                        </div>
                      ) : null)}
                    </div>
                    {p.content && (
                      <div className="mb-4">
                        <p className="text-white/22 text-[9px] uppercase tracking-wider mb-1">Full Details</p>
                        <p className="text-white/50 text-xs leading-relaxed whitespace-pre-wrap">{p.content}</p>
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 pt-3 border-t border-white/5">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditTarget(p); setShowModal(true); }}
                          className="px-3 py-1.5 rounded-lg bg-sky-700/25 text-sky-400 border border-sky-500/20 text-xs hover:bg-sky-700/40 transition-all"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                          className="px-3 py-1.5 rounded-lg bg-red-700/20 text-red-400 border border-red-500/20 text-xs hover:bg-red-700/35 transition-all"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit modal */}
      {showModal && (
        <PrecedentModal
          initial={editTarget}
          token={token}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
          onSaved={onSaved}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <DeleteConfirm
          title={deleteTarget.title}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
